'use client';
import React, { useEffect, useRef, useState } from 'react';

import { ImageStrapiProps } from '@/components/atoms/Image/ImageStrapi';
import { AccordionItemProps } from '@/components/molecules/accordion/AccordionItem';
import AccordionList from '@/components/molecules/accordion/AccordionList';

import { ProductCategoryTitlesProps } from './comparisonUtils/comparisonElements/ProductCategory';
import { ComparisonGridRow } from './comparisonUtils/comparisonSections/ComparisonRow';
import { ComparisonFeatureRows } from './comparisonUtils/comparisonSections/DesktopFeatureRow';
import { PriceActionRow } from './comparisonUtils/comparisonSections/PriceActionRow';
import { ProductComparisonItemProps } from './ProductComparisonTable';

export interface ProductComparisonTableProps {
  selectedData: ProductComparisonItemProps[];
  productTypeCategoryTitle?: string;
  productTypeCategoryTooltip?: React.ReactNode;
  keyFeaturesTitle?: string;
  keyFeaturesTooltip?: React.ReactNode;
  descriptionTitle?: string;
  descriptionTooltip?: React.ReactNode;
  productFeaturesCategoryTitles?: ProductCategoryTitlesProps[];
  productComparisonItems?: ProductComparisonItemProps[];
  accordionContent?: AccordionItemProps[];
  isBestValue?: boolean;
  productTitle?: string;
  productIcon?: ImageStrapiProps;
  stickyOffsetTop?: number;
}

export function DesktopComparisonGrid({
  selectedData,
  productTypeCategoryTitle,
  productTypeCategoryTooltip,
  keyFeaturesTitle,
  keyFeaturesTooltip,
  descriptionTitle,
  descriptionTooltip,
  productFeaturesCategoryTitles,
  accordionContent,
  stickyOffsetTop,
}: ProductComparisonTableProps) {
  const [headerHeights, setHeaderHeights] = useState<number[]>([0, 0, 0]);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const headerTopMarginOffset = 64;

  useEffect(() => {
    const heights = headerRefs.current.map((el) => el?.offsetHeight || 0);
    setHeaderHeights(heights);
  }, [selectedData]);

  const filteredData = selectedData.filter(Boolean);
  const visibleCount = filteredData.length;

  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const onScroll = () => {
      const rect = sentinel.getBoundingClientRect();
      setIsSticky(rect.top < headerTopMarginOffset);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <div>
        {!!productTypeCategoryTitle && (
          <ComparisonGridRow
            title={productTypeCategoryTitle}
            tooltip={productTypeCategoryTooltip}
            cellContents={filteredData.map((d) => d?.productType?.productType)}
            visibleItemsCount={visibleCount}
          />
        )}
        {!!keyFeaturesTitle && (
          <ComparisonGridRow
            title={keyFeaturesTitle}
            tooltip={keyFeaturesTooltip}
            cellContents={filteredData.map((d) => d?.productType?.features)}
            visibleItemsCount={visibleCount}
          />
        )}
        {!!descriptionTitle && (
          <ComparisonGridRow
            title={descriptionTitle}
            tooltip={descriptionTooltip}
            cellContents={filteredData.map((d) => d?.productType?.description)}
            visibleItemsCount={visibleCount}
          />
        )}
        <>
          <div ref={sentinelRef} className='h-1' />
          <div
            className='z-10'
            style={{
              position: 'sticky',
              top: (stickyOffsetTop ?? 0) + headerTopMarginOffset,
            }}
          >
            <PriceActionRow
              selectedData={selectedData}
              headerHeights={headerHeights}
              stickyOffsetTop={stickyOffsetTop}
              isSticky={isSticky}
            />
          </div>
        </>

        <ComparisonFeatureRows
          titles={productFeaturesCategoryTitles}
          selectedData={selectedData}
        />

        {accordionContent && (
          <div className='mt-12'>
            <AccordionList
              accordionList={accordionContent}
              isMultiMode={true}
            />
          </div>
        )}
      </div>
    </>
  );
}
